/**
 * IndexedDB cru, sem biblioteca.
 *
 * São ~80 linhas de invólucro em cima de uma API baseada em eventos. Uma
 * dependência (idb, Dexie) resolveria o mesmo, mas isto não muda com o tempo e
 * a decisão foi não trazer dependência nova.
 *
 * Um object store só, `cache`, com registros `{ key, data, cachedAt }`. Não é
 * um banco relacional local: guarda o resultado das mesmas listas que o app já
 * pede hoje. Consultar linha a linha localmente seria outro projeto, e a
 * leitura offline não precisa disso.
 */

const DB_NAME = 'appfiv';
const DB_VERSION = 2;
const STORE = 'cache';
// Fila de escritas pendentes. `seq` é autoincremental e define a ordem de
// reenvio — que precisa ser respeitada: um serviço tem que existir antes da
// sessão de OPU dele, e save_fiv_session/save_te_session travam uma na outra
// se rodarem em paralelo.
const OUTBOX = 'outbox';

let dbPromise = null;

/** IndexedDB pode não existir (aba anônima, navegador antigo, WebView restrita). */
const available = () => typeof indexedDB !== 'undefined';

function openDb() {
  if (!available()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(OUTBOX)) {
        db.createObjectStore(OUTBOX, { keyPath: 'seq', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn('[cache] IndexedDB indisponível:', req.error?.message);
      resolve(null);
    };
  });
  return dbPromise;
}

/** Promessa em cima de uma transação; nunca rejeita — cache é acessório. */
function tx(mode, fn, storeName = STORE) {
  return openDb().then(db => {
    if (!db) return null;
    return new Promise((resolve) => {
      let result = null;
      const t = db.transaction(storeName, mode);
      const store = t.objectStore(storeName);
      try {
        const req = fn(store);
        if (req) req.onsuccess = () => { result = req.result; };
      } catch (err) {
        console.warn('[cache] falha na transação:', err.message);
      }
      t.oncomplete = () => resolve(result);
      t.onerror = () => { console.warn('[cache] transação abortada:', t.error?.message); resolve(null); };
      t.onabort = () => resolve(null);
    });
  });
}

/** Registro guardado, ou null. */
export const readEntry = (key) => tx('readonly', s => s.get(key));

export const writeEntry = (key, data) =>
  tx('readwrite', s => s.put({ key, data, cachedAt: Date.now() }));

export const clearCache = () => tx('readwrite', s => s.clear());

// ── Fila de escrita ────────────────────────────────────────────────────────
//
// Diferente do cache, aqui a falha NÃO pode ser engolida: se a gravação na
// fila não deu certo, o lançamento do técnico se perdeu, e ele precisa saber
// disso na hora. Por isso estas usam `txStrict`.

/** Como `tx`, mas rejeita em vez de devolver null. */
function txStrict(mode, fn, storeName) {
  return openDb().then(db => {
    if (!db) throw new Error('Armazenamento local indisponível neste navegador');
    return new Promise((resolve, reject) => {
      let result = null;
      const t = db.transaction(storeName, mode);
      const store = t.objectStore(storeName);
      const req = fn(store);
      if (req) req.onsuccess = () => { result = req.result; };
      t.oncomplete = () => resolve(result);
      t.onerror = () => reject(t.error || new Error('falha ao gravar na fila'));
      t.onabort = () => reject(t.error || new Error('gravação na fila abortada'));
    });
  });
}

/** Enfileira e devolve o `seq` atribuído. */
export const pushOutbox = (record) =>
  txStrict('readwrite', s => s.add(record), OUTBOX);

/** Toda a fila, em ordem de `seq` (a própria ordem de inserção do store). */
export const readOutbox = () =>
  tx('readonly', s => s.getAll(), OUTBOX).then(rows => rows || []);

export const removeFromOutbox = (seq) =>
  txStrict('readwrite', s => s.delete(seq), OUTBOX);

export const updateOutbox = (record) =>
  txStrict('readwrite', s => s.put(record), OUTBOX);

export const clearOutbox = () => tx('readwrite', s => s.clear(), OUTBOX);

/** Data da cópia local mais recente, para a tela poder dizer de quando é. */
export const newestCachedAt = () =>
  tx('readonly', s => s.getAll(), STORE).then(rows =>
    (rows || []).reduce((max, r) => Math.max(max, r.cachedAt || 0), 0) || null);
